import { Box } from '@material-ui/core';
import { LoadingScreen } from 'components/Loaders';
import {
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Document,
  Page,
  pdfjs,
} from 'react-pdf';
import PropTypes from 'prop-types';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const PDFViewer = (props) => {
  const {
    url,
    setError,
    pdfConfig,
    setPdfConfig,
    modal,
  } = props;

  const containerRef = useRef(null);
  const pageRefs = useRef([]);
  const [lastScrollTime, setLastScrollTime] = useState(0);
  const isScrolling = useRef(false);
  const [manualScroll, setManualScroll] = useState(false);

  // Efecto para hacer scroll a la página seleccionada
  useEffect(() => {
    if (pageRefs.current[pdfConfig.pageNumber - 1] && containerRef.current && !isScrolling.current) {
      const now = Date.now();
      if (now - lastScrollTime < 300) return;

      const elemento = document.getElementById(`page_${pdfConfig.pageNumber}`);
      elemento.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });

      setManualScroll(true);

      const pageElement = pageRefs.current[pdfConfig.pageNumber - 1];
      const container = containerRef.current;

      const pageRect = pageElement.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const scrollTo = pageRect.top - containerRect.top + container.scrollTop;

      container.scrollTo({
        top: scrollTo,
        behavior: 'smooth',
      });

      setTimeout(() => {
        setManualScroll(false);
      }, 500);

      setLastScrollTime(now);
    } else {
      isScrolling.current = false;
    }
  }, [pdfConfig.pageNumber]);

  // Detectar la página visible mediante el evento de scroll
  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current || manualScroll) return;

      const container = containerRef.current;
      let mostVisiblePage = null;
      let maxVisibleHeight = 0;

      pageRefs.current.forEach((page, index) => {
        if (!page) return;
        const rect = page.getBoundingClientRect();
        const visibleHeight = Math.min(rect.bottom, container.clientHeight) - Math.max(rect.top, 0);

        if (visibleHeight > maxVisibleHeight) {
          maxVisibleHeight = visibleHeight;
          mostVisiblePage = index;
        }
      });

      if (mostVisiblePage !== null && mostVisiblePage + 1 !== pdfConfig.pageNumber) {
        isScrolling.current = true;
        setPdfConfig((prev) => ({
          ...prev,
          pageNumber: mostVisiblePage + 1,
        }));
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
  }, [pdfConfig.pageNumber, manualScroll]);

  return (
    <Box
      ref={containerRef}
      style={{
        display: 'grid',
        placeItems: 'center',
        width: '-webkit-fill-available',
        height: '100%',
        ...(modal && {
          maxHeight: '75dvh',
        }),
        overflow: 'auto',
        position: 'relative',
        padding: 0,
      }}
    >
      <Document
        file={url}
        loading={<LoadingScreen />}
        onLoadSuccess={({ numPages }) => {
          setPdfConfig((prev) => ({
            ...prev,
            numPages: numPages,
          }));
          pageRefs.current = Array(numPages).fill().map((_, i) => pageRefs.current[i] || null);
        }}
        onLoadError={(error) => {
          console.error('PDF error:', error);
          setError('Error al cargar el PDF');
        }}
      >
        {Array.from({
          length: pdfConfig.numPages || 0,
        }, (_, index) => (
          <Box
            id={`page_${index + 1}`}
            key={`page_${index + 1}`}
            ref={(el) => pageRefs.current[index] = el}
            data-page-index={index}
            style={{
              margin: '10px',
              scrollMargin: '20px',
            }}
          >
            <Page
              pageNumber={index + 1}
              width={pdfConfig.width}
            />
          </Box>
        ))}
      </Document>
    </Box>
  );
};

PDFViewer.defaultProps = {
  setError: () => { },
  pdfConfig: {
    pageNumber: 1,
    numPages: 0,
    width: 800,
  },
  setPdfConfig: () => { },
  modal: false,
};

PDFViewer.propTypes = {
  url: PropTypes.string.isRequired,
  setError: PropTypes.func,
  pdfConfig: PropTypes.object,
  setPdfConfig: PropTypes.func,
  modal: PropTypes.bool,
};

export default PDFViewer;
